/**
 * Argus's connection to Talkie over the direct bridge (`@talkie/agentic-app-transport`). Build
 * artifact: `frontend/bridge.bundle.js` (`npm run build`). The handlers live in `tools.ts`; this file
 * registers them and the snapshot resource, starts the bridge, and tells app.js how it is going.
 *
 * Host bootstrap: the host appends `#v=1&k=<key>&o=<origin>` to the iframe URL. Without it the page
 * is an ordinary standalone console; with it the page says hello and the status reads "connected"
 * only once the host has welcomed it.
 */

import { AppBridge, type BridgeStatus } from "@talkie/agentic-app-transport";
import { APP } from "./app-identity";
import { HANDLERS, readSnapshot, SNAPSHOT_RESOURCE } from "./tools";

/** What app.js shows in the "Talkie:" status chip. */
export interface BridgeState { mode: BridgeStatus; reason?: string }

declare global {
  interface Window {
    __argusBridgeState?: BridgeState;
  }
}

/**
 * Retained AND announced: app.js imports this module, so this code runs before app.js has a
 * listener. A late reader takes `window.__argusBridgeState`; a listener gets `argus:bridge`.
 */
function publish(state: BridgeState): void {
  window.__argusBridgeState = state;
  window.dispatchEvent(new CustomEvent("argus:bridge", { detail: state }));
}

const bridge = new AppBridge({ appVersion: APP.version, onStatus: (mode, reason) => publish({ mode, reason }) });
for (const [name, handler] of Object.entries(HANDLERS)) bridge.registerTool(name, handler as Parameters<AppBridge["registerTool"]>[1]);
bridge.registerResource(SNAPSHOT_RESOURCE, readSnapshot);
bridge.start();
